import React, { useCallback } from 'react';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { Edition } from '@atlassian/touchpoint-commons-event-analytics';

import { useEditionAwareness } from '../../services/edition-awareness-context';
import { useGetExperimentValue } from '../../services/experiment-context';
import { isEligibleForEAtoOSBFreeWithPDE } from '../../services/is-eligible-for-ea-to-osb-free-with-pde';
import type { LinkConfig } from '../dialogs/shared/types';
import { EditionAwarenessLink } from '../edition-awareness-button';

export const LINK_CONFIG: LinkConfig = {
	linkId: 'upgrade-flow',
	touchpointIntent: 'changePlan',
	targetProductEdition: Edition.STANDARD_EDITION,
};

const UpgradeStandardFlowLinkContent = (): React.JSX.Element => {
	const context = useEditionAwareness();
	const { changeOfferingToStandardExperienceUrl, product } = context;
	const { getExperimentValue } = useGetExperimentValue();

	const handleUpgradeFlowClick = useCallback(
		(
			e: React.MouseEvent<HTMLAnchorElement>,
			_analyticsEvent: UIAnalyticsEvent,
			tracedUrl: string,
		) => {
			if (isEligibleForEAtoOSBFreeWithPDE(context)) {
				getExperimentValue({
					statsigParams: {
						experimentName: 'ea-to-osb-free-with-pde-ab',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
				});
			}

			if (product === 'jira' && tracedUrl) {
				try {
					e.preventDefault();
					const url = new URL(tracedUrl);
					// Read window.location.href at click time so it reflects any client-side
					// navigation that occurred since the component was rendered, regardless of
					// which router (Jira or Confluence) is in use.
					url.searchParams.set('returnUrl', window.location.href);
					window.open(url.toString(), '_blank');
				} catch {
					// URL construction failed — let the <a> tag navigate naturally
				}
			}
		},
		[product, context, getExperimentValue],
	);

	return (
		<EditionAwarenessLink
			href={changeOfferingToStandardExperienceUrl!}
			target="_blank"
			onClick={handleUpgradeFlowClick}
			{...LINK_CONFIG}
		/>
	);
};

export const UpgradeStandardFlowLink = (): React.JSX.Element => {
	return (
		<AnalyticsContext data={LINK_CONFIG}>
			<UpgradeStandardFlowLinkContent />
		</AnalyticsContext>
	);
};
